import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "../components/Reveal";
import { ArticleBody } from "../components/journal/ArticleBody";
import { MoreNotes } from "../components/journal/MoreNotes";
import { JournalCta } from "../components/journal/JournalCta";
import { Seo } from "../components/Seo";
import { useT } from "../i18n/I18nProvider";

export default function JournalArticle() {
  const { slug } = useParams();
  const t = useT();
  const entries = t("journal.entries");
  const entry = entries.find((e) => e.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!entry) return <Navigate to="/journal" replace />;

  const others = entries.filter((e) => e.slug !== slug).slice(0, 2);
  const readLabel = t("journal.readTimeLabel");
  const readUnit = t("journal.readTimeUnit");

  return (
    <>
      <Seo
        page="journal"
        titleOverride={`${entry.title} · Mysterious Stew`}
        descriptionOverride={entry.excerpt}
      />
      {/* HEADER */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pt-12 md:pt-20 pb-10 md:pb-14">
        <Reveal>
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 overline hover:text-ink-900 transition-colors"
            data-testid="article-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6} />
            {t("journal.backLabel")}
          </Link>
        </Reveal>
        <Reveal delay={60}>
          <div className="mt-8 md:mt-12 flex flex-wrap items-center gap-4">
            <span className="overline">{entry.date}</span>
            <span className="text-[10px] md:text-[11px] font-mono tracking-[0.18em] uppercase px-3 py-1.5 border border-ink-200 rounded-full text-ink-700">
              {entry.tag}
            </span>
            <span className="overline">
              {readLabel} · {entry.readTime} {readUnit}
            </span>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <h1
            className="mt-5 md:mt-8 font-display text-4xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tightest max-w-[18ch]"
            data-testid="article-title"
          >
            {entry.title}
          </h1>
        </Reveal>
      </section>

      {/* BODY */}
      <section className="border-t border-ink-200">
        <ArticleBody entry={entry} tagLabel={entry.tag} />
      </section>

      {/* MORE NOTES */}
      <MoreNotes entries={others} />

      {/* CTA */}
      <JournalCta testId="article-cta" />
    </>
  );
}
